// Realtime inbox data + mutations (PRD §4.7 / WP-04). Subscribes to the tenant-scoped
// conversations/messages with Firestore listeners and maps them into canonical
// LiveConversation and InboxMessage shapes. All writes go through the callables.
import { useCallback, useEffect, useState } from 'react';
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { stripUndefined } from './callable';
import { useAuth } from './useAuth';
import { useTenant } from './tenant';
import type {
  ConversationFilter,
  ConversationFolder,
  ConversationStatus,
  InboxMessage,
  LiveConversation,
  MsgStatus,
} from './inboxTypes';

export * from './inboxTypes';

export const DEFAULT_FILTER: ConversationFilter = {
  folder: 'all',
  botStatus: 'all',
  sortBy: 'recent_reply',
  query: '',
  status: 'all',
  assignee: 'all',
  label: 'all',
  unreadOnly: false,
};

/** Pure predicate — keeps the list filtering testable and index-free. */
export function conversationMatchesFilter(conv: LiveConversation, f: Partial<ConversationFilter>): boolean {
  // 1. Folder check
  const folder: ConversationFolder = f.folder ?? 'all';
  if (folder === 'archived') {
    if (!conv.archived) return false;
  } else if (folder === 'blocked') {
    if (!conv.blocked) return false;
  } else {
    // Normal folders hide archived and blocked conversations
    if (conv.archived || conv.blocked) return false;

    if (folder === 'starred' && !conv.starred) return false;
    if (folder === 'unread' && conv.unread <= 0) return false;
    if (folder === 'resolved' && conv.status !== 'resolved') return false;
    if (folder === 'mine' && conv.assignee !== 'You') return false;
    // In 'all', we don't show resolved conversations by default unless status is set
    if (folder === 'all' && conv.status === 'resolved' && f.status !== 'resolved') return false;
  }

  // 2. Legacy / granular status filter
  if (f.status && f.status !== 'all' && conv.status !== f.status) return false;

  // 3. Unread filter
  if (f.unreadOnly && conv.unread <= 0) return false;

  // 4. Label filter
  if (f.label && f.label !== 'all' && !conv.labels.includes(f.label)) return false;

  // 5. Assignee filter
  if (f.assignee === 'unassigned' && conv.assignee) return false;
  if (f.assignee === 'mine' && conv.assignee !== 'You') return false;
  if (f.assignee && f.assignee !== 'all' && f.assignee !== 'mine' && f.assignee !== 'unassigned' && conv.assignee !== f.assignee) {
    return false;
  }

  // 6. Bot reply status filter
  if (f.botStatus === 'replying' && (conv.botPaused || !conv.botReply)) return false;
  if (f.botStatus === 'paused' && !conv.botPaused && conv.botReply) return false;

  // 7. Integration filter
  if (f.integrationId && f.integrationId !== 'ALL_ACCOUNTS' && (conv.integrationId ?? 'primary') !== f.integrationId) {
    return false;
  }

  // 8. Text search query
  if (f.query && f.query.trim()) {
    const q = f.query.trim().toLowerCase();
    const searchTarget = `${conv.name} ${conv.displayName ?? ''} ${conv.phone} ${conv.preview}`.toLowerCase();
    if (!searchTarget.includes(q)) return false;
  }

  return true;
}

// ── mapping ──────────────────────────────────────────────────────────────────
const PALETTE = ['bg-primary', 'bg-blue-500', 'bg-amber-500', 'bg-rose-500', 'bg-emerald-600', 'bg-indigo-500'];
function colorFor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
}
function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || '?';
}
function fmtTime(ms?: number): string {
  if (!ms) return '';
  return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
function fmtDay(ms?: number): string {
  if (!ms) return '';
  const d = new Date(ms);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  if (sameDay) return 'today';
  if (d.toDateString() === yest.toDateString()) return 'yesterday';
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' });
}
function mapStatus(s?: string): MsgStatus | undefined {
  if (s === 'read' || s === 'delivered' || s === 'failed') return s;
  if (s === 'sent' || s === 'sandbox' || s === 'queued' || s === 'simulated') return 'sent';
  return undefined;
}

function mapConversation(d: any, myUid: string): LiveConversation {
  const name: string = d.customerName || d.displayName || d.phoneE164 || 'Unknown';
  const win = typeof d.windowExpiresAt === 'number' ? Math.round((d.windowExpiresAt - Date.now()) / 60000) : null;
  const assignedUid = d.assignedTo ?? d.assignee ?? null;
  const assignee = assignedUid ? (assignedUid === myUid ? 'You' : assignedUid) : null;

  // Determine last sender badge
  let lastSender: LiveConversation['lastSender'] = undefined;
  if (d.lastMessageDirection === 'in') {
    lastSender = 'user';
  } else if (d.lastMessageDirection === 'out') {
    lastSender = d.lastMessageSource === 'bot' || d.botReplyEnabled ? 'bot' : 'agent';
  }

  return {
    id: d.id,
    integrationId: (d.integrationId as string) ?? 'primary',
    name,
    displayName: d.displayName ?? null,
    phone: d.phoneE164 ?? '',
    initials: initialsFor(d.displayName || name),
    avatarColor: colorFor(d.id ?? name),
    channel: d.channel === 'messenger' ? 'messenger' : 'whatsapp',
    preview: d.lastMessageBody ?? '',
    time: fmtTime(d.lastMessageAt ?? d.updatedAt),
    unread: d.unreadCount ?? 0,
    assignee,
    assignedTo: assignedUid,
    labels: Array.isArray(d.labels) ? d.labels : Array.isArray(d.labelIds) ? d.labelIds : [],
    windowMinsLeft: win,
    windowExpiresAt: typeof d.windowExpiresAt === 'number' ? d.windowExpiresAt : (typeof win === 'number' ? Date.now() + win * 60000 : null),
    botReply: d.botReplyEnabled !== undefined ? Boolean(d.botReplyEnabled) : !d.botPaused,
    aiAgent: Boolean(d.aiReplyEnabled),
    botPaused: Boolean(d.botPaused),
    aiPaused: Boolean(d.aiPaused),
    starred: Boolean(d.starred),
    archived: Boolean(d.archived),
    blocked: Boolean(d.blocked),
    status: (d.status as ConversationStatus) ?? 'open',
    customFields: Array.isArray(d.customFields) ? d.customFields : [],
    subscribedAt: d.createdAt
      ? new Date(d.createdAt).toLocaleDateString([], { day: '2-digit', month: 'short', year: '2-digit' })
      : '',
    lastSender,
    lastMessageStatus: mapStatus(d.lastMessageStatus ?? d.lastStatus),
    lastMessageAt: d.lastMessageAt,
    lastInboundAt: d.lastInboundAt,
    updatedAt: d.updatedAt,
    createdAt: d.createdAt,
  };
}

function mapMessage(d: any): InboxMessage {
  const mediaUrl: string | null = d.mediaUrl ?? null;
  const hasMedia = Boolean(mediaUrl || d.mediaId);
  const direction = d.direction === 'note' ? 'note' : d.direction === 'out' ? 'out' : 'in';
  const templateName = d.templateName ?? null;
  const templateVariables = Array.isArray(d.templateVariables) ? d.templateVariables : null;

  let body = d.body ?? '';
  if (d.type === 'call') {
    body = d.body || '📞 WhatsApp Call';
  } else if (hasMedia && !mediaUrl && !body) {
    body = '[media]';
  } else if (!body && templateName) {
    const varsText = templateVariables && templateVariables.length > 0 ? ` (${templateVariables.join(', ')})` : '';
    body = `📋 ${templateName}${varsText}`;
  }

  const error = d.error ? {
    code: d.error.code ? String(d.error.code) : null,
    message: d.error.message ? String(d.error.message) : null,
  } : null;

  return {
    id: d.id,
    direction,
    body,
    time: fmtTime(d.createdAt),
    status: direction === 'out' ? mapStatus(d.status) : undefined,
    error,
    templateName,
    templateLanguage: d.templateLanguage ?? null,
    templateVariables,
    day: fmtDay(d.createdAt),
    mediaUrl,
    mediaType: (d.mediaType as InboxMessage['mediaType']) ?? (d.type === 'call' ? null : null),
    agentReaction: d.agentReaction ?? null,
    customerReaction: d.customerReaction ?? null,
    source: d.source ?? null,
    type: d.type ?? (direction === 'note' ? 'note' : 'text'),
    callDuration: d.callDuration,
    createdAt: d.createdAt,
    translatedBody: d.translatedBody,
  };
}

// ── realtime hooks ───────────────────────────────────────────────────────────
export function useConversations() {
  const { tenantId } = useTenant();
  const { user } = useAuth();
  const [conversations, setConversations] = useState<LiveConversation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!tenantId) {
      setConversations([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, `tenants/${tenantId}/conversations`),
      orderBy('updatedAt', 'desc'),
      limit(300),
    );
    return onSnapshot(
      q,
      (snap) => {
        setConversations(snap.docs.map((doc) => mapConversation({ id: doc.id, ...doc.data() }, user?.uid ?? '')));
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, [tenantId, user?.uid]);

  return { conversations, loading };
}

export function useMessages(conversationId: string | null, initialLimit: number = 50) {
  const { tenantId } = useTenant();
  const [messages, setMessages] = useState<InboxMessage[]>([]);
  const [currentLimit, setCurrentLimit] = useState<number>(initialLimit);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    setCurrentLimit(initialLimit);
  }, [conversationId, initialLimit]);

  useEffect(() => {
    if (!tenantId || !conversationId) {
      setMessages([]);
      setLoading(false);
      setHasMore(false);
      return;
    }
    const q = query(
      collection(db, `tenants/${tenantId}/conversations/${conversationId}/messages`),
      orderBy('createdAt', 'desc'),
      limit(currentLimit + 1),
    );
    return onSnapshot(
      q,
      (snap) => {
        const rawDocs = snap.docs;
        if (rawDocs.length > currentLimit) {
          setHasMore(true);
          // Omit the extra one and reverse so oldest is first in display order
          const sliced = rawDocs.slice(0, currentLimit).map((doc) => mapMessage({ id: doc.id, ...doc.data() }));
          setMessages(sliced.reverse());
        } else {
          setHasMore(false);
          const mapped = rawDocs.map((doc) => mapMessage({ id: doc.id, ...doc.data() }));
          setMessages(mapped.reverse());
        }
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, [tenantId, conversationId, currentLimit]);

  const loadMore = useCallback(() => {
    setCurrentLimit((prev) => prev + 50);
  }, []);

  return { messages, loading, hasMore, loadMore };
}

export interface ConversationNote {
  id: string;
  text: string;
  byEmail: string | null;
  createdAt: number;
}

export function useConversationNotes(conversationId: string | null) {
  const { tenantId } = useTenant();
  const [notes, setNotes] = useState<ConversationNote[]>([]);
  useEffect(() => {
    if (!tenantId || !conversationId) {
      setNotes([]);
      return;
    }
    const q = query(
      collection(db, `tenants/${tenantId}/conversations/${conversationId}/notes`),
      orderBy('createdAt', 'desc'),
    );
    return onSnapshot(q, (snap) => setNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() } as ConversationNote))));
  }, [tenantId, conversationId]);
  return notes;
}

// ── mutation wrappers (callables) ────────────────────────────────────────────
function call<TReq, TRes = { ok: boolean }>(name: string) {
  const fn = httpsCallable<TReq, TRes>(functions, name);
  return (data: TReq) => fn(stripUndefined(data)).then((r) => r.data);
}

export const updateConversation = call<{
  conversationId: string;
  status?: ConversationStatus;
  assignee?: string | null;
  assignedTo?: string | null;
  labels?: string[];
  labelIds?: string[];
  starred?: boolean;
  archived?: boolean;
  blocked?: boolean;
  displayName?: string | null;
  unreadCount?: number;
  unread?: boolean;
  botPaused?: boolean;
  aiPaused?: boolean;
  botReply?: boolean;
  aiReply?: boolean;
}>('whatsappUpdateConversation');

export const markConversationRead = call<{ conversationId: string }>('whatsappMarkConversationRead');

export const setConversationFlags = call<{
  conversationId: string;
  botReply?: boolean;
  aiReply?: boolean;
  botPaused?: boolean;
  aiPaused?: boolean;
}>('whatsappSetConversationFlags');

export const addConversationNote = call<{ conversationId: string; text: string }>('whatsappAddConversationNote');

export const deleteConversation = call<{ conversationId: string }>('whatsappDeleteConversation');

export const sendReaction = call<{ conversationId: string; messageId: string; emoji: string }>('whatsappSendReaction');

export { sendMessage } from './whatsappApi';
